import { LockIcon } from "lucide-react";
import type { JSONContent } from "@tiptap/react";

import { CommentComposer } from "./CommentComposer";

import { Text } from "@/components";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useCreateInternalCandidacyComment } from "@/hooks/mutations/useCreateInternalCandidacyComment";
import { useSetNewInternalCandidacyCommentInInternalCandidacyComments } from "@/hooks/sets/useSetNewInternalCandidacyCommentInInternalCandidacyComments";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

export const InternalCommentComposer = ({
  candidacyId,
  candidateName,
  commentDraft,
  onCommentDraftChange,
}: {
  candidacyId: string;
  candidateName: string;
  commentDraft: JSONContent;
  onCommentDraftChange: (contentJson: JSONContent) => void;
}) => {
  /* Database CRUD Operations */
  // Create
  const { isPending, mutate: addComment } = useCreateInternalCandidacyComment();

  /* Cache Updates */
  // Set
  const setNewCommentInComments =
    useSetNewInternalCandidacyCommentInInternalCandidacyComments();

  /* React Hooks */
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();

  /* Render */
  const render = () => {
    return (
      <CommentComposer
        banner={
          <>
            <LockIcon className="h-3.5 w-3.5 shrink-0" />
            <Text className="min-w-0 truncate text-xs font-medium text-slate-500">
              Internal · not visible to {candidateName}
            </Text>
          </>
        }
        draft={commentDraft}
        isPending={isPending}
        onDraftChange={onCommentDraftChange}
        onSend={(contentJson, reset) => {
          mixpanelUtils.trackFormSubmission(
            "Add comment",
            MIXPANEL_SCREENS.CANDIDATE_SHEET,
          );
          addComment(
            {
              candidacyId,
              createInternalCandidacyCommentRequest: { contentJson },
              currentCompanyId,
              currentJobId,
            },
            {
              onSuccess: (newComment) => {
                reset();
                setNewCommentInComments({
                  candidacyId,
                  internalCandidacyCommentToAdd: newComment,
                });
              },
            },
          );
        }}
        placeholder="Write a comment..."
      />
    );
  };

  return render();
};
